/**
 * VoiceStudioOverlay — professional voice-to-design recording UI.
 *
 * Pipeline:
 *   record  →  live waveform + timer
 *           →  stop  →  send to /api/voice/transcribe
 *           →  show "I heard X" preview (editable)
 *           →  user confirms or re-records
 *           →  onConfirm(text)
 *
 * Replaces the previous "tap mic, instant set chatMessage" UX which gave the
 * user no chance to verify what the AI heard.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mic, MicOff, Square, X, Check, RotateCcw, Loader2, Edit3, AlertCircle, Languages,
} from 'lucide-react';
import { API_URL } from '@/utils/apiUrl';
import NexusRealtimeVoice from '@/components/NexusRealtimeVoice';

const EnhanceChip = ({ onClick, active, disabled, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs font-mono border transition-colors ${
      active
        ? 'border-cyan-400 text-cyan-300 bg-cyan-500/10'
        : 'border-white/15 text-white/70 hover:border-white/40'
    } disabled:opacity-50`}
  >
    {children}
  </button>
);


const MAX_RECORD_SECONDS = 60;

const SPEECH_LOCALES = {
  tr: 'tr-TR',
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  ar: 'ar-SA',
  zh: 'zh-CN',
  ja: 'ja-JP',
  ko: 'ko-KR',
  pt: 'pt-BR',
  ru: 'ru-RU',
  hi: 'hi-IN',
};

const speechLocale = (language) => SPEECH_LOCALES[language] || 'en-US';

const fmtTime = (sec) => {
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

export default function VoiceStudioOverlay({ open, language = 'tr', onClose, onConfirm, onDesignRequest, onNavigate, onInspectPage }) {
  const [stage, setStage] = useState('idle'); // idle | recording | transcribing | review | error
  const [elapsed, setElapsed] = useState(0);
  const [transcribed, setTranscribed] = useState('');
  const [detectedLanguage, setDetectedLanguage] = useState(language);
  const [error, setError] = useState(null);
  // Live transcript preview — Web Speech API runs in parallel with MediaRecorder
  // so the user sees their words appearing IN REAL TIME while we still send the
  // higher-quality Whisper transcription on stop. Best of both worlds.
  const [livePartial, setLivePartial] = useState('');
  const [liveFinal,   setLiveFinal]   = useState('');
  // Audio level (0-100) for the volume meter
  const [audioLevel, setAudioLevel] = useState(0);
  // Quick enhancement state on review
  const [enhancing, setEnhancing] = useState(null); // null | 'detail' | 'pro' | 'shorten'

  // Refs for media & analyser
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  // Web Speech API live recognition (runs in parallel with MediaRecorder)
  const liveRecognitionRef = useRef(null);
  const liveFinalAccRef = useRef('');

  const T = useMemo(() => language === 'tr' ? {
    title: 'Nexus Sesli Komut',
    sub: 'Nexus’a ne yapmak istediğinizi söyleyin — proje, şirket analizi veya satın alma akışını kendi seçsin',
    startHint: 'Mikrofona dokunun ve Nexus’a komutunuzu söyleyin',
    recording: 'Kaydediliyor',
    listening: 'Sizi duyuyorum…',
    stop: 'Durdur',
    cancel: 'İptal',
    transcribing: 'Sesiniz çözümleniyor…',
    iHeard: 'Şunu duydum',
    iHeardSub: 'Doğru ise onaylayın, değilse düzeltin veya yeniden kaydedin',
    redo: 'Yeniden kaydet',
    use: 'Bu metni kullan',
    micDenied: 'Mikrofon erişimi reddedildi. Tarayıcı izinlerini kontrol edin.',
    transcribeFail: 'Ses çözümlenemedi. Tekrar deneyin veya doğrudan yazın.',
    autoLang: 'Otomatik dil algılama',
    tr: 'Türkçe', en: 'English',
    enhanceTitle: 'AI ile zenginleştir',
    enhanceDetail: 'Daha detaylı',
    enhancePro: 'Profesyonel',
    enhanceShorten: 'Kısalt',
    enhanceRunning: 'AI metni zenginleştiriyor…',
    volume: 'Ses',
  } : {
    title: 'Nexus Voice Command',
    sub: 'Tell Nexus what you need — it can route projects, company analysis, and purchasing workflows',
    startHint: 'Tap the microphone and tell Nexus what you want to accomplish',
    recording: 'Recording',
    listening: 'Listening…',
    stop: 'Stop',
    cancel: 'Cancel',
    transcribing: 'Transcribing your voice…',
    iHeard: 'Here is what I heard',
    iHeardSub: 'Confirm if correct, otherwise edit or re-record',
    redo: 'Re-record',
    use: 'Use this text',
    micDenied: 'Microphone access denied. Check your browser permissions.',
    transcribeFail: 'Could not transcribe audio. Try again or type directly.',
    autoLang: 'Auto language detection',
    tr: 'Türkçe', en: 'English',
    enhanceTitle: 'Enhance with AI',
    enhanceDetail: 'Add detail',
    enhancePro: 'Make pro',
    enhanceShorten: 'Shorten',
    enhanceRunning: 'AI is enhancing your text…',
    volume: 'Volume',
  }, [language]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      stopAll();
      setStage('idle');
      setElapsed(0);
      setTranscribed('');
      setError(null);
      setLivePartial('');
      setLiveFinal('');
      setAudioLevel(0);
      setEnhancing(null);
      liveFinalAccRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopAll = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    } catch (e) {}
    try { if (liveRecognitionRef.current) liveRecognitionRef.current.stop(); } catch (e) {}
    try { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
    try { if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') audioCtxRef.current.close(); } catch (e) {}
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    streamRef.current = null;
    mediaRecorderRef.current = null;
    liveRecognitionRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    rafRef.current = null;
    elapsedTimerRef.current = null;
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (canvas.width !== w * dpr) { canvas.width = w * dpr; canvas.height = h * dpr; ctx.scale(dpr, dpr); }
    const bufferLength = analyser.fftSize;
    const data = new Uint8Array(bufferLength);

    const tick = () => {
      analyser.getByteTimeDomainData(data);
      ctx.fillStyle = '#060B1A';
      ctx.fillRect(0, 0, w, h);

      // Compute RMS audio level for the volume meter
      let sumSquares = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = (data[i] - 128) / 128;
        sumSquares += v * v;
      }
      const rms = Math.sqrt(sumSquares / bufferLength);
      const lvl = Math.min(100, Math.round(rms * 280));
      // Throttle React state update — every 4th frame is plenty for a meter
      if ((rafRef.current ?? 0) % 4 === 0) setAudioLevel(lvl);

      // Center line
      ctx.strokeStyle = 'rgba(34, 211, 238, 0.12)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2); ctx.stroke();

      // Waveform
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0,   '#22D3EE');
      grad.addColorStop(0.5, '#10B981');
      grad.addColorStop(1,   '#22D3EE');
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = grad;
      ctx.beginPath();
      const sliceWidth = w / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = data[i] / 128.0;
        const y = (v * h) / 2;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        x += sliceWidth;
      }
      ctx.stroke();
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  // Spin up the browser SpeechRecognition in parallel with MediaRecorder.
  // It's best-effort — if the browser doesn't support it we just skip the
  // live transcript preview; Whisper still runs on stop for high accuracy.
  const startLiveRecognition = () => {
    const SpeechAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechAPI) return;
    try {
      const rec = new SpeechAPI();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = speechLocale(language);
      rec.onresult = (e) => {
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) {
            liveFinalAccRef.current = (liveFinalAccRef.current + ' ' + r[0].transcript).trim();
          } else {
            interim += r[0].transcript;
          }
        }
        setLiveFinal(liveFinalAccRef.current);
        setLivePartial(interim);
      };
      rec.onerror = () => { /* swallow — Whisper is still recording */ };
      rec.onend = () => { /* may end while user is still talking; restart if needed */
        if (mediaRecorderRef.current?.state === 'recording') {
          try { rec.start(); } catch (e) {}
        }
      };
      rec.start();
      liveRecognitionRef.current = rec;
    } catch (e) { /* ignore */ }
  };

  const startRecording = async () => {
    setError(null);
    setStage('recording');
    setElapsed(0);

    // Capability detection — fall back to browser SpeechRecognition when
    // getUserMedia / MediaRecorder is unavailable (some embedded webviews,
    // older browsers, locked-down corporate envs).
    const hasMR = typeof window !== 'undefined'
      && navigator.mediaDevices?.getUserMedia
      && typeof window.MediaRecorder !== 'undefined';
    if (!hasMR) {
      return startBrowserSpeechFallback();
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Audio analyser for waveform
      const AC = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AC();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;
      // start draw on next tick (canvas may not be mounted yet)
      setTimeout(drawWaveform, 60);

      // Recorder
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = handleStopFinish;
      mr.start();

      // Start the parallel live-transcript preview (best effort)
      liveFinalAccRef.current = '';
      setLiveFinal('');
      setLivePartial('');
      startLiveRecognition();

      // Timer
      elapsedTimerRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e + 1 >= MAX_RECORD_SECONDS) {
            try { mr.stop(); } catch (err) {}
          }
          return e + 1;
        });
      }, 1000);
    } catch (err) {
      console.error(err);
      // Last-resort fallback: try browser speech recognition
      const SpeechAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechAPI) {
        return startBrowserSpeechFallback();
      }
      setError(T.micDenied);
      setStage('error');
    }
  };

  // Browser-native SpeechRecognition fallback — no waveform, no MediaRecorder.
  // Goes directly to "review" with whatever the browser transcribed.
  const startBrowserSpeechFallback = () => {
    const SpeechAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechAPI) {
      setError(T.micDenied);
      setStage('error');
      return;
    }
    try {
      const recognition = new SpeechAPI();
      recognition.lang = speechLocale(language);
      recognition.continuous = false;
      recognition.interimResults = false;
      setStage('transcribing');
      recognition.onresult = (e) => {
        const text = e.results?.[0]?.[0]?.transcript || '';
        if (!text.trim()) {
          setError(T.transcribeFail);
          setStage('error');
          return;
        }
        setTranscribed(text);
        setDetectedLanguage(language);
        setStage('review');
      };
      recognition.onerror = () => {
        setError(T.micDenied);
        setStage('error');
      };
      recognition.start();
    } catch (err) {
      setError(T.micDenied);
      setStage('error');
    }
  };

  const handleStopFinish = async () => {
    setStage('transcribing');
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    try { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}

    const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    try {
      const fd = new FormData();
      fd.append('audio_file', blob, 'recording.webm');
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const voiceLanguage = String(language || 'en').toLowerCase().split(/[-_]/)[0];
      const res = await fetch(`${API_URL}/api/voice/transcribe?language=${encodeURIComponent(voiceLanguage)}`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const text = (data && data.text) || '';
      if (!text.trim()) throw new Error('empty transcription');
      setTranscribed(text);
      // best-effort language detection from server, falls back to UI lang
      if (data && data.language) setDetectedLanguage(data.language);
      setStage('review');
    } catch (err) {
      console.error(err);
      setError(T.transcribeFail);
      setStage('error');
    }
  };

  const stopNow = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleRedo = () => {
    setTranscribed('');
    setStage('idle');
    setElapsed(0);
    setLiveFinal('');
    setLivePartial('');
    setAudioLevel(0);
    setEnhancing(null);
    liveFinalAccRef.current = '';
  };

  // Quick AI enhancement chips (detail / pro / shorten) — runs gpt-4o
  // server-side rewrite of the transcribed prompt.
  const runEnhance = async (style) => {
    if (!transcribed.trim() || enhancing) return;
    setEnhancing(style);
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/voice/enhance`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text: transcribed, style, language: detectedLanguage || language }),
      });
      const d = await res.json();
      if (d && d.success && d.text) setTranscribed(d.text);
    } catch (e) { /* keep original text on error */ }
    finally { setEnhancing(null); }
  };

  const handleUse = () => {
    if (transcribed.trim()) onConfirm(transcribed.trim(), detectedLanguage);
  };

  return (
      <div
        data-testid="voice-studio-overlay"
        hidden={!open}
        aria-hidden={!open}
        style={{ display: open ? 'flex' : 'none' }}
        className="fixed inset-0 z-[210] items-center justify-center bg-black/80 backdrop-blur-md p-4"
      >
        <motion.div
          initial={{ y: 28, scale: 0.97, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 16, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 240 }}
          className="w-full max-w-2xl overflow-hidden rounded-2xl border border-cyan-500/30 bg-[#060B1A] shadow-[0_0_80px_-20px_rgba(34,211,238,0.7)]"
        >
          {/* Header */}
          <div className="relative border-b border-white/10 bg-gradient-to-br from-cyan-500/10 to-emerald-500/5 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-2">
                  <Mic className="h-5 w-5 text-cyan-300" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white" data-testid="voice-overlay-title">{T.title}</h2>
                  <p className="mt-1 text-xs text-white/60">{T.sub}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={language === 'tr' ? 'Sesli komut penceresini gizle' : 'Hide voice command window'}
                className="rounded-lg p-1 text-white/40 transition hover:bg-white/5 hover:text-white"
                data-testid="voice-overlay-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-8">
            {/* IDLE — big mic button */}
            {stage === 'idle' && (
              <div className="flex flex-col items-center text-center" data-testid="voice-stage-idle">
                <NexusRealtimeVoice
                  language={language}
                  onDesignRequest={onDesignRequest}
                  onNavigate={onNavigate}
                  onInspectPage={onInspectPage}
                />
                <div className="mb-6 flex w-full items-center gap-3 text-[10px] uppercase tracking-widest text-white/30">
                  <span className="h-px flex-1 bg-white/10" />
                  {language === 'tr' ? 'veya tek komut kaydet' : 'or record one command'}
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <p className="mb-7 text-sm text-white/60">{T.startHint}</p>
                <motion.button
                  whileTap={{ scale: 0.94 }}
                  whileHover={{ scale: 1.03 }}
                  onClick={startRecording}
                  className="group relative grid h-28 w-28 place-items-center rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 text-[#060B1A] shadow-[0_0_50px_-10px_rgba(34,211,238,0.7)]"
                  data-testid="voice-start-btn"
                >
                  <span className="absolute inset-0 animate-ping rounded-full bg-cyan-400/30" />
                  <Mic className="h-12 w-12 relative z-10" strokeWidth={2.4} />
                </motion.button>
                <div className="mt-6 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40">
                  <Languages className="h-3 w-3" /> {T.autoLang}
                </div>
              </div>
            )}

            {/* RECORDING — waveform + timer + stop */}
            {stage === 'recording' && (
              <div data-testid="voice-stage-recording">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="relative inline-flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400/70" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" />
                    </span>
                    <span className="text-sm font-semibold text-rose-300">
                      {audioLevel > 4 ? T.listening : T.recording}
                    </span>
                  </div>
                  <div className="font-mono text-2xl font-bold text-white" data-testid="voice-timer">
                    {fmtTime(elapsed)} <span className="text-white/30 text-sm">/ {fmtTime(MAX_RECORD_SECONDS)}</span>
                  </div>
                </div>

                <canvas
                  ref={canvasRef}
                  className="h-28 w-full rounded-xl border border-white/10 bg-[#060B1A]"
                  data-testid="voice-waveform-canvas"
                />

                {/* Volume meter — gives the user confidence the mic is hot */}
                <div className="mt-3 flex items-center gap-3" data-testid="voice-volume-meter">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">{T.volume}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-100 ${
                        audioLevel > 60 ? 'bg-rose-400' :
                        audioLevel > 25 ? 'bg-emerald-400' :
                        audioLevel > 8  ? 'bg-cyan-400'    : 'bg-white/20'
                      }`}
                      style={{ width: `${Math.max(2, audioLevel)}%` }}
                    />
                  </div>
                  <span className="text-[10px] font-mono tabular-nums text-white/40 w-8 text-right">{audioLevel}</span>
                </div>

                {/* Live transcript preview — Web Speech API streaming */}
                {(liveFinal || livePartial) && (
                  <div
                    data-testid="voice-live-transcript"
                    className="mt-4 max-h-32 overflow-y-auto rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 text-sm leading-relaxed"
                  >
                    <div className="mb-1 flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-cyan-300/70">
                      <span className="inline-block h-1 w-1 rounded-full bg-cyan-400 animate-pulse" />
                      LIVE TRANSCRIPT
                    </div>
                    <span className="text-white/90">{liveFinal}</span>{' '}
                    <span className="text-white/50 italic">{livePartial}</span>
                  </div>
                )}

                <div className="mt-6 flex items-center justify-center gap-3">
                  <button
                    onClick={onClose}
                    className="rounded-full border border-white/10 px-5 py-2.5 text-sm text-white/70 transition hover:border-white/30 hover:text-white"
                    data-testid="voice-cancel-btn"
                  >
                    {T.cancel}
                  </button>
                  <motion.button
                    whileTap={{ scale: 0.96 }}
                    onClick={stopNow}
                    className="inline-flex items-center gap-2 rounded-full bg-rose-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-rose-500/30 hover:bg-rose-400"
                    data-testid="voice-stop-btn"
                  >
                    <Square className="h-4 w-4 fill-white" />
                    {T.stop}
                  </motion.button>
                </div>
              </div>
            )}

            {/* TRANSCRIBING — spinner */}
            {stage === 'transcribing' && (
              <div className="flex flex-col items-center py-10 text-center" data-testid="voice-stage-transcribing">
                <Loader2 className="h-9 w-9 animate-spin text-cyan-400" />
                <p className="mt-4 text-sm text-white/70">{T.transcribing}</p>
              </div>
            )}

            {/* REVIEW — editable transcription */}
            {stage === 'review' && (
              <div data-testid="voice-stage-review">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-300">
                    <Check className="h-3.5 w-3.5" /> {T.iHeard}
                  </div>
                  <div className="flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/60">
                    <Languages className="h-3 w-3" />
                    {detectedLanguage === 'tr' ? T.tr : T.en}
                  </div>
                </div>
                <p className="mb-3 text-xs text-white/50">{T.iHeardSub}</p>

                <textarea
                  data-testid="voice-transcript-textarea"
                  value={transcribed}
                  onChange={(e) => setTranscribed(e.target.value)}
                  rows={4}
                  className="w-full resize-none rounded-xl border border-cyan-500/30 bg-cyan-500/5 px-4 py-3 text-base text-white outline-none focus:border-cyan-400 disabled:opacity-60"
                  disabled={!!enhancing}
                  autoFocus
                />

                {/* Quick AI enhancement chips */}
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="voice-enhance-chips">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-white/40">
                    {T.enhanceTitle}
                  </span>
                  <EnhanceChip onClick={() => runEnhance('detail')} active={enhancing==='detail'} disabled={!!enhancing} testId="voice-enhance-detail">
                    {T.enhanceDetail}
                  </EnhanceChip>
                  <EnhanceChip onClick={() => runEnhance('pro')} active={enhancing==='pro'} disabled={!!enhancing} testId="voice-enhance-pro">
                    {T.enhancePro}
                  </EnhanceChip>
                  <EnhanceChip onClick={() => runEnhance('shorten')} active={enhancing==='shorten'} disabled={!!enhancing} testId="voice-enhance-shorten">
                    {T.enhanceShorten}
                  </EnhanceChip>
                  {enhancing && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-cyan-300">
                      <Loader2 className="h-3 w-3 animate-spin" /> {T.enhanceRunning}
                    </span>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-between gap-3">
                  <button
                    onClick={handleRedo}
                    className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 transition hover:border-white/30 hover:text-white"
                    data-testid="voice-redo-btn"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {T.redo}
                  </button>
                  <button
                    onClick={handleUse}
                    disabled={!transcribed.trim() || !!enhancing}
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-emerald-500 px-5 py-2 text-sm font-semibold text-[#060B1A] shadow-lg transition hover:shadow-cyan-500/30 disabled:opacity-40"
                    data-testid="voice-use-btn"
                  >
                    <Check className="h-4 w-4" />
                    {T.use}
                  </button>
                </div>
              </div>
            )}

            {/* ERROR */}
            {stage === 'error' && (
              <div className="flex flex-col items-center py-8 text-center" data-testid="voice-stage-error">
                <div className="rounded-full border border-rose-500/30 bg-rose-500/10 p-3">
                  <AlertCircle className="h-7 w-7 text-rose-400" />
                </div>
                <p className="mt-3 text-sm text-rose-200">{error}</p>
                <div className="mt-5 flex gap-3">
                  <button onClick={onClose} className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 hover:text-white">
                    {T.cancel}
                  </button>
                  <button onClick={handleRedo} className="rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-[#060B1A] hover:bg-cyan-400">
                    {T.redo}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
  );
}
